/**
 * API client for communicating with the backend
 */

// Default to localhost:4000 in development if VITE_API_URL is not set
export const API_BASE = import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV ? "http://localhost:4000" : "");

// Tenant ID resolved at runtime — sent as header so the API resolves the correct tenant
import { resolvedTenantId } from "@/lib/resolveTenant";
import {
  reportServiceHealthFailure,
  reportServiceHealthSuccess,
} from "@/lib/serviceHealth";
const TENANT_ID = resolvedTenantId;

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiResponse<T> {
  data: T;
  correlationId: string | null;
}

export interface RequestOptions {
  token?: string | null;
  correlationId?: string | null;
}

const isAbortError = (error: unknown): boolean =>
  error instanceof Error && error.name === "AbortError";

const reportResponseHealth = (response: Response): void => {
  if (response.ok) {
    reportServiceHealthSuccess();
    return;
  }

  if (response.status >= 500) {
    reportServiceHealthFailure("server");
  }
};

const fetchWithServiceHealth = async (
  url: string,
  options: RequestInit
): Promise<Response> => {
  try {
    const response = await fetch(url, options);
    reportResponseHealth(response);
    return response;
  } catch (error) {
    if (!isAbortError(error)) {
      reportServiceHealthFailure("network");
    }
    throw error;
  }
};

const request = async <T>(
  endpoint: string,
  options: RequestInit = {},
  token?: string | null,
  correlationId?: string | null
): Promise<T> => {
  const url = `${API_BASE}${endpoint}`;

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(TENANT_ID ? { "X-Tenant-Id": TENANT_ID } : {}),
    ...(options.headers || {}),
  };

  if (token) {
    (headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
  }

  if (correlationId) {
    (headers as Record<string, string>)["X-Correlation-Id"] = correlationId;
  }

  const response = await fetchWithServiceHealth(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = await response.text();
    }
    throw new ApiError(
      (body as { error?: string })?.error ||
        `Request failed with status ${response.status}`,
      response.status,
      body
    );
  }

  return response.json();
};

/**
 * Make a request and return both data and correlation ID.
 * Use this for hotel flows where you need to track the correlation ID
 * from autocomplete through search/details/booking.
 */
const requestWithCorrelation = async <T>(
  endpoint: string,
  options: RequestInit = {},
  requestOptions: RequestOptions = {}
): Promise<ApiResponse<T>> => {
  const url = `${API_BASE}${endpoint}`;

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(TENANT_ID ? { "X-Tenant-Id": TENANT_ID } : {}),
    ...(options.headers || {}),
  };

  if (requestOptions.token) {
    (headers as Record<string, string>)["Authorization"] = `Bearer ${requestOptions.token}`;
  }

  if (requestOptions.correlationId) {
    (headers as Record<string, string>)["X-Correlation-Id"] = requestOptions.correlationId;
  }

  const response = await fetchWithServiceHealth(url, {
    ...options,
    headers,
  });

  // Capture correlation ID from response
  const responseCorrelationId = response.headers.get("X-Correlation-Id");

  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = await response.text();
    }
    throw new ApiError(
      (body as { error?: string })?.error ||
        `Request failed with status ${response.status}`,
      response.status,
      body
    );
  }

  const data = await response.json();
  return { data, correlationId: responseCorrelationId };
};

export const api = {
  get: <T>(endpoint: string, token?: string | null, correlationId?: string | null) =>
    request<T>(endpoint, { method: "GET" }, token, correlationId),

  post: <T>(endpoint: string, data?: unknown, token?: string | null, correlationId?: string | null) =>
    request<T>(
      endpoint,
      {
        method: "POST",
        body: data ? JSON.stringify(data) : undefined,
      },
      token,
      correlationId
    ),

  patch: <T>(
    endpoint: string,
    data?: unknown,
    token?: string | null,
    correlationId?: string | null
  ) =>
    request<T>(
      endpoint,
      { method: "PATCH", body: data ? JSON.stringify(data) : undefined },
      token,
      correlationId
    ),

  delete: <T>(endpoint: string, token?: string | null, correlationId?: string | null) =>
    request<T>(endpoint, { method: "DELETE" }, token, correlationId),

  /**
   * GET request that returns correlation ID for session tracking.
   * Use for autocomplete to capture the ID for subsequent requests.
   */
  getWithCorrelation: <T>(endpoint: string, opts: RequestOptions = {}) =>
    requestWithCorrelation<T>(endpoint, { method: "GET" }, opts),

  /**
   * POST request that returns correlation ID for session tracking.
   * Pass correlationId from previous autocomplete call.
   */
  postWithCorrelation: <T>(endpoint: string, data?: unknown, opts: RequestOptions = {}) =>
    requestWithCorrelation<T>(
      endpoint,
      { method: "POST", body: data ? JSON.stringify(data) : undefined },
      opts
    ),
};

export { ApiError };
